import React, { FC, useEffect } from 'react'
import LoginFormBody from '../common/LoginFormBody/LoginFormBody'
import AppFormControl from '../../../components/AppForm/AppFormControl'
import InputLabel from '@mui/material/InputLabel'
import { Controller, useForm } from 'react-hook-form'
import { REGEX_CONFIRM_CODE_VALIDATION } from '../../../constants/regex'
import {
	Box,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	FormHelperText,
	Link,
	OutlinedInput,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import {
	selectAuthenticatorId,
	selectMfaToken,
	selectOOBChannel,
	selectOOBCode,
} from '../../../store/reducers/auth/selectors'
import {
	completeLogin,
	mfaChallenge,
	setSkipMfaAuth,
} from '../../../store/reducers/auth/actions'
import { PAGE_LOGIN } from '../../../constants/navigation'

import { history } from '../../../store'
import { useTypedSelector } from '../../../hooks/useTypedSelector'
import { useDefaultComponentTools } from '../../../hooks/useDefaultComponentTools'

interface ILogin2FACodeForm {
	bindingCode: string
	rememberFor30Days: boolean
}

const BINDING_CODE_FIELD_NAME = 'bindingCode'
const REMEMBER_FOR_30_DAYS_FIELD_NAME = 'rememberFor30Days'

const Login2FACodeForm: FC = () => {
	const { loading, setLoading, isMounted, dispatch, alert } =
		useDefaultComponentTools()
	const oobChannel = useTypedSelector(selectOOBChannel)
	const mfaToken = useTypedSelector(selectMfaToken)
	const oobCode = useTypedSelector(selectOOBCode)
	const authenticatorId = useTypedSelector(selectAuthenticatorId)
	const {
		handleSubmit,
		control,
		formState: { errors, isValid },
	} = useForm<ILogin2FACodeForm>({
		mode: 'all',
		defaultValues: {
			[BINDING_CODE_FIELD_NAME]: '',
			[REMEMBER_FOR_30_DAYS_FIELD_NAME]: false,
		},
	})

	useEffect(() => {
		if (!oobCode) {
			history.replace(PAGE_LOGIN)
		}
	}, [oobCode])

	const confirmCodeError = errors[BINDING_CODE_FIELD_NAME]?.message || ''

	const isSubmitButtonDisabled = loading || !isValid

	const phoneOrEmail = oobChannel === 'sms' ? 'phone' : 'email'

	const onSubmit = async (data: ILogin2FACodeForm): Promise<any> => {
		try {
			if (!mfaToken) return

			setLoading(true)

			dispatch(setSkipMfaAuth(!data[REMEMBER_FOR_30_DAYS_FIELD_NAME]))

			await dispatch(
				completeLogin(oobCode, data[BINDING_CODE_FIELD_NAME], mfaToken)
			)
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		} finally {
			isMounted() && setLoading(false)
		}
	}

	const handleResendCode = async (): Promise<void> => {
		try {
			if (!mfaToken) {
				alert.error('Invalid mfa token.')

				return
			}

			await dispatch(mfaChallenge(authenticatorId, mfaToken))

			alert.success(`Code was sent to your ${phoneOrEmail}`)
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		}
	}

	return (
		<LoginFormBody title={'Login'}>
			<Typography
				align={'center'}
				paragraph
				sx={{ fontSize: '1.125rem', mt: 0, whiteSpace: 'break-spaces' }}
			>
				<span>{`Please enter the code we\n sent to your ${phoneOrEmail}`}</span>

				<Box
					component={Link}
					sx={{ ml: 1, cursor: 'pointer' }}
					onClick={handleResendCode}
				>
					Resend code
				</Box>
			</Typography>

			<form onSubmit={handleSubmit(onSubmit)}>
				<AppFormControl
					sx={{ mb: 7 }}
					color={'primary'}
					error={!!confirmCodeError}
				>
					<InputLabel shrink htmlFor={'login-email'}>
						Code
					</InputLabel>

					<Controller
						control={control}
						rules={{
							required: 'Please enter the code.',
							pattern: REGEX_CONFIRM_CODE_VALIDATION,
						}}
						render={({ field }) => (
							<OutlinedInput
								id={'login-2FA-code'}
								{...field}
								type={'text'}
							/>
						)}
						name={BINDING_CODE_FIELD_NAME}
					/>
					{!!confirmCodeError && (
						<FormHelperText id={'login-2FA-code'}>
							{confirmCodeError}
						</FormHelperText>
					)}
				</AppFormControl>

				<FormControlLabel
					control={
						<Controller
							control={control}
							defaultValue={false}
							render={({ field }) => <Checkbox {...field} />}
							name={REMEMBER_FOR_30_DAYS_FIELD_NAME}
						/>
					}
					label='Remember me for 30 days'
					sx={{ maxWidth: '16rem' }}
				/>
				<Button
					size={'large'}
					variant='contained'
					type={'submit'}
					color={'primary'}
					sx={{ maxWidth: '8.75rem' }}
					disabled={!!isSubmitButtonDisabled}
				>
					{loading ? <CircularProgress size={28} /> : 'Login'}
				</Button>
			</form>
		</LoginFormBody>
	)
}

export default Login2FACodeForm
